import React, { useEffect, useMemo, useState } from 'react'
import Icon from '../../../components/AppIcon'
import Button from '../../../components/ui/Button'
import { fetchGoldTransactions, inLastDays, isEnvelopeReady, isAwaitingReport, isCancelledTx, usd, grams } from '../lib/goldTx'
import { useMetalPrices } from '../hooks/useMetalPrices'

const WINDOWS = [
  { days: 1, label: 'Last 24 hours' },
  { days: 7, label: 'Last 7 days' },
  { days: 15, label: 'Last 15 days' },
  { days: 30, label: 'Last 30 days' },
]

function windowStats(txs, days) {
  const rows = txs.filter((t) => inLastDays(t, days))
  return {
    count: rows.length,
    weight: rows.reduce((s, t) => s + t.weightGrams, 0),
    pureAu: rows.reduce((s, t) => s + t.goldPureGrams, 0),
    payout: rows.reduce((s, t) => s + t.payout, 0),
  }
}

export default function ExchangeDashboard({ onNavigate }) {
  const [txs, setTxs] = useState(null)
  const [error, setError] = useState('')
  const [now, setNow] = useState(() => new Date())
  const { prices, loading: pricesLoading } = useMetalPrices()

  useEffect(() => {
    let alive = true
    const clock = window.setInterval(() => setNow(new Date()), 60000)
    fetchGoldTransactions()
      // Cancelled sales are void — they don't count toward purchases or envelopes.
      .then((rows) => { if (alive) setTxs(rows.filter((t) => !isCancelledTx(t))) })
      .catch((e) => { if (alive) setError(e?.message || 'Failed to load transactions') })
    return () => { alive = false; window.clearInterval(clock) }
  }, [])

  const stats = useMemo(() => {
    if (!txs) return null
    return WINDOWS.map((w) => ({ ...w, ...windowStats(txs, w.days) }))
  }, [txs])

  const readyEnvelopes = useMemo(
    () => (txs || []).filter((t) => !t.claimedAt && isEnvelopeReady(t, now)),
    [txs, now]
  )
  const pendingEnvelopes = useMemo(
    () => (txs || []).filter((t) => !t.claimedAt && isAwaitingReport(t)),
    [txs]
  )
  const holdingEnvelopes = useMemo(
    () => (txs || []).filter((t) => !t.claimedAt && !isAwaitingReport(t) && !isEnvelopeReady(t, now)),
    [txs, now]
  )

  if (error) {
    return <div className="bg-card rounded-2xl border border-border p-6 text-sm text-destructive">{error}</div>
  }
  if (!txs) {
    return <div className="bg-card rounded-2xl border border-border p-8 text-center text-muted-foreground text-sm">Loading dashboard…</div>
  }

  return (
    <div className="space-y-4">
      {/* Envelope-ready alert */}
      {readyEnvelopes.length > 0 ? (
        <div className="rounded-2xl border border-success/40 bg-success/10 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-success/15 flex items-center justify-center text-success flex-shrink-0">
              <Icon name="PackageCheck" size={20} />
            </div>
            <div>
              <p className="font-semibold text-success">
                {readyEnvelopes.length} envelope{readyEnvelopes.length === 1 ? '' : 's'} ready to use
              </p>
              <p className="text-sm text-success/90">
                The 30-day hold has passed — this AU can be claimed now.
              </p>
            </div>
          </div>
          <Button variant="success" onClick={() => onNavigate && onNavigate('envelopes')} iconName="ArrowRight" iconPosition="right">
            Open Envelopes
          </Button>
        </div>
      ) : (
        <div className="rounded-2xl border border-border bg-card p-4 flex items-center gap-3">
          <Icon name="Package" size={18} className="text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            No envelopes ready yet — {pendingEnvelopes.length} to report · {holdingEnvelopes.length} on the 30-day hold.
          </p>
        </div>
      )}

      {/* Purchase windows */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {stats.map((w) => (
          <div key={w.days} className="bg-card rounded-2xl border border-border p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{w.label}</p>
            <p className="text-3xl font-bold text-foreground mt-2">{w.count}</p>
            <p className="text-sm text-muted-foreground -mt-0.5 mb-3">purchase{w.count === 1 ? '' : 's'}</p>
            <div className="space-y-1 text-sm border-t border-border/60 pt-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Weight</span>
                <span className="text-foreground font-medium">{grams(w.weight)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Pure AU (24K)</span>
                <span className="text-foreground font-medium">{grams(w.pureAu)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Paid out</span>
                <span className="text-foreground font-semibold">{usd(w.payout)}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Price + envelope pipeline strip */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <div className="bg-card rounded-2xl border border-border p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Gold price now</p>
          <p className="text-2xl font-bold text-foreground">
            {pricesLoading ? '…' : prices?.gold ? `${usd(prices.gold)}/g` : 'Offline'}
          </p>
          {prices && (
            <p className={`text-xs mt-1 ${prices.stale ? 'text-warning' : 'text-success'}`}>
              {prices.stale ? 'Stale price' : 'Live'}{prices.ageMinutes != null ? ` · ${prices.ageMinutes} min ago` : ''}
            </p>
          )}
        </div>
        <div className="bg-card rounded-2xl border border-border p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">To report</p>
          <p className="text-2xl font-bold text-foreground">{pendingEnvelopes.length}</p>
          <p className="text-xs text-muted-foreground mt-1">Hold starts when the report is sent</p>
        </div>
        <div className="bg-card rounded-2xl border border-border p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Envelopes on hold</p>
          <p className="text-2xl font-bold text-foreground">{holdingEnvelopes.length}</p>
          <p className="text-xs text-muted-foreground mt-1">Waiting the 30-day period</p>
        </div>
        <div className="bg-card rounded-2xl border border-border p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">All-time purchases</p>
          <p className="text-2xl font-bold text-foreground">{txs.length}</p>
          <p className="text-xs text-muted-foreground mt-1">{grams(txs.reduce((s, t) => s + t.weightGrams, 0))} total</p>
        </div>
      </div>
    </div>
  )
}
